import React, { useEffect, useState } from 'react';
import { organizationService } from '../../services/api';

/**
 * Organization settings for the clinic's parent org (e.g. team collaboration mode).
 * Saves via PATCH /api/organizations/{id} independently of clinic settings edit mode.
 */
const OrganizationSection = ({ organizationId }) => {
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!organizationId) {
      setOrg(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await organizationService.getOrganization(organizationId);
        if (!cancelled) setOrg(data);
      } catch (err) {
        console.error('Error loading organization:', err);
        if (!cancelled) {
          setError(err.response?.data?.detail || err.message || 'Failed to load organization');
          setOrg(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const handleToggleTeamCollaboration = async (enabled) => {
    if (!organizationId || saving) return;
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const data = await organizationService.patchOrganization(organizationId, {
        team_collaboration_mode: enabled,
      });
      setOrg(data);
      setSuccess(
        enabled
          ? 'Team collaboration mode enabled for this organization.'
          : 'Team collaboration mode disabled for this organization.'
      );
    } catch (err) {
      console.error('Error updating organization settings:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to update organization');
    } finally {
      setSaving(false);
    }
  };

  if (!organizationId) {
    return (
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h4 className="text-md font-semibold mb-2">Organization</h4>
        <p className="text-sm text-gray-500">This clinic has no organization_id.</p>
      </div>
    );
  }

  const teamCollaborationMode = Boolean(org?.settings?.team_collaboration_mode);

  return (
    <div className="mt-6 pt-6 border-t border-gray-200">
      <h4 className="text-md font-semibold mb-4">Organization</h4>
      {loading && <p className="text-sm text-gray-500">Loading organization…</p>}
      {error && (
        <p className="mb-2 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      {success && (
        <p className="mb-2 text-sm text-green-700" role="status">
          {success}
        </p>
      )}
      {org && (
        <div className="space-y-3">
          <p className="flex items-center text-sm">
            <span className="font-medium w-40 text-gray-700">Organization ID:</span>
            <span className="font-mono text-gray-900">{org.id}</span>
          </p>
          <p className="flex items-center text-sm">
            <span className="font-medium w-40 text-gray-700">Name:</span>
            <span className="text-gray-900">{org.name}</span>
          </p>
          <div className="flex items-start">
            <div className="flex items-center">
              <input
                id="team_collaboration_mode"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={teamCollaborationMode}
                disabled={saving}
                onChange={(e) => handleToggleTeamCollaboration(e.target.checked)}
              />
              <label htmlFor="team_collaboration_mode" className="ml-2 text-sm font-medium text-gray-700">
                Team collaboration mode
              </label>
            </div>
          </div>
          <p className="text-xs text-gray-500 max-w-xl">
            When enabled, nexi-hub uses a WATI/Sleekflow-style full-screen WhatsApp inbox with
            shared assignment (All / Mine / Unassigned) for every clinic under this organization.
          </p>
        </div>
      )}
    </div>
  );
};

export default OrganizationSection;
